import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { ShieldCheck, Warning } from "@phosphor-icons/react";

export default function SecurityPolicy() {
  const nav = useNavigate();
  const [pol, setPol] = useState(null);
  const [requireMfa, setRequireMfa] = useState(false);
  const [graceDays, setGraceDays] = useState(7);
  const [idleMin, setIdleMin] = useState(60);
  const [warnMin, setWarnMin] = useState(5);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/security-policy").then((r) => {
    setPol(r.data);
    setRequireMfa(!!r.data.require_mfa);
    setGraceDays(r.data.grace_days ?? 7);
    setIdleMin(r.data.session_idle_minutes ?? 60);
    setWarnMin(r.data.session_warning_minutes ?? 5);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/security-policy", {
        require_mfa: requireMfa, grace_days: Number(graceDays),
        session_idle_minutes: Number(idleMin), session_warning_minutes: Number(warnMin),
      });
      setPol(data);
      setIdleMin(data.session_idle_minutes);
      setWarnMin(data.session_warning_minutes);
      toast.success("Security policy saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the policy.");
      setRequireMfa(!!pol?.require_mfa); // revert toggle on failure (e.g. admin lockout guard)
    } finally {
      setSaving(false);
    }
  };

  if (!pol) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const adminReady = !!pol.admin_mfa_ready;
  const dirty = requireMfa !== !!pol.require_mfa || Number(graceDays) !== (pol.grace_days ?? 7)
    || Number(idleMin) !== (pol.session_idle_minutes ?? 60) || Number(warnMin) !== (pol.session_warning_minutes ?? 5);

  return (
    <div className="max-w-2xl space-y-6" data-testid="security-policy">
      <div className="border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} className="mt-0.5 text-primary shrink-0" />
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg">Require multi-factor authentication</h3>
            <p className="text-sm text-muted-foreground mt-1">
              When on, every user in this entity must set up an authenticator-app (TOTP) login.
              Users are given a grace period to enroll; after it lapses they must set up MFA before they can sign in.
            </p>
          </div>
          <Switch data-testid="require-mfa-switch" checked={requireMfa}
            onCheckedChange={setRequireMfa} disabled={!adminReady && !requireMfa} />
        </div>

        {!adminReady && (
          <div data-testid="admin-mfa-warning" className="mt-4 flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2.5">
            <Warning size={18} className="mt-0.5 shrink-0" />
            <span>
              You must enable MFA on <strong>your own account</strong> before requiring it for everyone — this prevents locking yourself out.{" "}
              <button className="underline font-medium" data-testid="goto-account-security" onClick={() => nav("/account")}>Set up my MFA</button>.
            </span>
          </div>
        )}

        <div className="mt-5 flex items-end gap-3">
          <div>
            <Label htmlFor="grace-days">Grace period (days)</Label>
            <Input id="grace-days" data-testid="grace-days-input" type="number" min={0} max={365}
              value={graceDays} onChange={(e) => setGraceDays(e.target.value)}
              className="rounded-none mt-1 w-28" disabled={!requireMfa} />
            <p className="text-xs text-muted-foreground mt-1">Days users have to enroll before MFA is enforced at login. 0 = immediate.</p>
          </div>
          <Button data-testid="save-security-policy" onClick={save} disabled={saving || !dirty}
            className="rounded-none bg-primary hover:bg-primary/90">{saving ? "Saving…" : "Save policy"}</Button>
        </div>

        {pol.require_mfa && pol.enforced_at && (
          <div data-testid="policy-enforced-note" className="mt-4 text-xs text-muted-foreground">
            Required since {new Date(pol.enforced_at).toLocaleDateString()} · grace period {pol.grace_days} day(s).
          </div>
        )}
      </div>

      <div className="border border-border bg-card p-6" data-testid="session-timeout-card">
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} className="mt-0.5 text-primary shrink-0" />
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg">Inactivity session timeout</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Automatically sign users out after a period of inactivity. A countdown warning appears before
              the session ends so active users can stay signed in. Applies to everyone in this workspace.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="idle-min">Inactivity timeout (minutes)</Label>
            <Input id="idle-min" data-testid="idle-minutes-input" type="number" min={5} max={1440}
              value={idleMin} onChange={(e) => setIdleMin(e.target.value)} className="rounded-none mt-1 w-32" />
            <p className="text-xs text-muted-foreground mt-1">No activity for this long → signed out.</p>
          </div>
          <div>
            <Label htmlFor="warn-min">Warning before timeout (minutes)</Label>
            <Input id="warn-min" data-testid="warning-minutes-input" type="number" min={1} max={Math.max(1, Number(idleMin) - 1)}
              value={warnMin} onChange={(e) => setWarnMin(e.target.value)} className="rounded-none mt-1 w-32" />
            <p className="text-xs text-muted-foreground mt-1">Countdown shown this long before logout.</p>
          </div>
          <Button data-testid="save-session-timeout" onClick={save} disabled={saving || !dirty}
            className="rounded-none bg-primary hover:bg-primary/90">{saving ? "Saving…" : "Save"}</Button>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Currently: sign out after <strong>{pol.session_idle_minutes ?? 60} min</strong> idle,
          with a <strong>{pol.session_warning_minutes ?? 5} min</strong> warning.
        </div>
      </div>
    </div>
  );
}
